
// Design System
import { PrimaryButton, SecondaryButton } from 'jl-design-system/elements/button/Button';
import { SecondaryLink } from 'jl-design-system/elements/link/Link';
import { LoadingSpinner } from 'jl-design-system/components/loading-spinner/LoadingSpinner';

// Types
import { EditBasketItemOverlayProps } from './EditBasketItemOverlay.types';

// Config
import editBasketConstants from '../../constants/editBasketConstants';
import urlConstants from '../../constants/urlConstants';
import { useHandbackToUrl } from '../../hooks/useHandbackToUrl';

// Components
import BodyText from '../body-text';
import Container from '../container';

// Styles
import styles from './edit-basket-item-overlay.scss';

const EditBasketItemOverlay = ({
  disabled = false,
  hasChildItems = false,
  lastItemInBasket = false,
  onRemoveNo,
  onRemoveYes,
  removeItemApiCallActive = false,
  shouldShowSaveForLater = false,
  type = editBasketConstants.REMOVE_ITEM,
  editBasketIsRemove,
}: EditBasketItemOverlayProps) => {
  const handbackProps = useHandbackToUrl(urlConstants.BASKET_PAGE_URL);
  const removeItemText = (shouldShowSaveForLater && !editBasketIsRemove) ?
    'Are you sure you want to save this item for later?' :
    'Are you sure you want to remove this item?';

  const containerProps = {
    flex: {
      alignItems: hasChildItems ? 'start' : 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    paddingTop: hasChildItems ? '8' : undefined,
    textAlign: 'center',
  } as const;
  const ctaWrapperProps = {
    flex: { flexItem: 'auto', flexWrap: 'wrap', justifyContent: 'center' },
  } as const;

  return (
    <Container
      className={`${styles.itemContainer} fade-in-animation`}
      {...containerProps}
    >
      {removeItemApiCallActive && (
        <LoadingSpinner data-testid="edit-basket-item-remove-spinner" />
      )}

      {!removeItemApiCallActive && (
        <>
          {(type === editBasketConstants.REMOVE_ITEM || type === editBasketConstants.REMOVE_TRADE_IN_ITEM) && (
            <Container testId="edit-basket-item-remove">
              <BodyText marginBottom="2" testId="edit-basket-item-remove-title">{removeItemText}</BodyText>

              <Container {...ctaWrapperProps}>
                <SecondaryButton
                  data-testid="edit-basket-item-remove-button-yes"
                  disabled={disabled}
                  inline
                  onClick={(args: any) => onRemoveYes({ ...args, forceRemove: editBasketIsRemove })}
                >
                  Yes
                </SecondaryButton>

                <SecondaryButton
                  data-testid="edit-basket-item-remove-button-no"
                  disabled={disabled}
                  inline
                  onClick={onRemoveNo}
                >
                  No
                </SecondaryButton>
              </Container>

              {(shouldShowSaveForLater && !editBasketIsRemove) && (
                <BodyText marginBottom="4">
                  Your item will be stored in your basket under &apos;Saved for later&apos;.
                </BodyText>
              )}

              {lastItemInBasket && (
                <BodyText testId="edit-basket-item-remove-last-item">
                  This will redirect you to the homepage as your basket will be empty.
                </BodyText>
              )}
            </Container>
          )}

          {(type === editBasketConstants.OUT_OF_STOCK_ITEM) && (
            <Container testId="edit-basket-item-out-of-stock">
              <BodyText marginBottom="2" testId="edit-basket-item-out-of-stock-title">
                Sorry, this item is no longer available. Please remove this item to continue.
              </BodyText>

              <Container {...ctaWrapperProps}>
                <PrimaryButton
                  data-testid="edit-basket-item-out-of-stock-remove"
                  disabled={disabled}
                  inline
                  onClick={onRemoveYes}
                >
                  Remove
                </PrimaryButton>

                {handbackProps && (
                  <SecondaryLink
                    data-testid="edit-basket-item-out-of-stock-return-to-basket"
                    disabled={disabled}
                    external
                    inline
                    {...handbackProps}
                  >
                    Return to basket
                  </SecondaryLink>
                )}
              </Container>

              {lastItemInBasket && (
                <BodyText testId="edit-basket-item-remove-last-item">
                  This will redirect you to the homepage as your basket will be empty.
                </BodyText>
              )}
            </Container>
          )}
        </>
      )}
    </Container>
  );
};

export default EditBasketItemOverlay;
